import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  Box,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import LensFilled from '@material-ui/icons/Lens';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { RefreshIcon } from '../../../../assets/Svg';
import ExpandMoreIcon from '../../../../assets/Svg/ExpandIcon';
import SearchTextField, { ISearchTextFieldImperativeHandle } from '../../../../components/SearchTextField';
import { IUserSegment } from '../../../../services/types';

const Accordion = withStyles({
  root: {
    width: '100%',
    backgroundColor: 'transparent',
    border: '0 none',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 0,
    },
  },
  expanded: { margin: 0 },
})(MuiAccordion);

const AccordionSummary = withStyles({
  root: {
    '&$expanded': {
      minHeight: 64,
    },
  },
  content: {
    margin: '12px 0',
    '&$expanded': {
      margin: '12px 0',
    },
  },

  expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles((theme: Theme) => ({
  root: {
    paddingTop: 0,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: 12,
    width: 380,
    maxHeight: 470,
    overflow: 'auto',
  },
}))(MuiAccordionDetails);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'fixed',
    bottom: 40,
    right: 40,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
    borderRadius: '2px',
    boxShadow: '0 24px 24px 0 rgba(0, 0, 0, 0.3), 0 0 24px 0 rgba(0, 0, 0, 0.22)',
    zIndex: 3,
  },
  statusIcon: {
    fontSize: '0.75rem',
    marginRight: 2,
  },
  active: {
    color: '#00d29c',
  },
  queued: {
    color: theme.palette.primary.main,
  },
  inactive: {
    color: '#d6d6d6',
  },
  itemTitle: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 210,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: 11,
  },
  internalTitleEmphasize: {
    fontWeight: 500,
    color: theme.palette.primary.main,
    fontSize: 10,
  },
  idAvatar: {
    display: 'inline-block',
    fontSize: '0.75rem',
    width: 20,
    height: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    backgroundColor: theme.palette.primary.dark,
    lineHeight: 1.8,
    paddingLeft: 1,
    marginRight: 10,
  },
  defaultTitle: {
    fontWeight: 500,
    color: theme.palette.text.disabled,
  },
}));

interface IProps {
  onChangeSelectedIds: (ids: number[]) => void;
  onChangeUserId: (userId: string) => void;
  forceRefresh: () => void;
  segmentList: IUserSegment[];
  selectedIds: number[];
  allUserId?: number;
}

export const Control = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const searchRef = useRef<ISearchTextFieldImperativeHandle>();

  // states
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [segmentList, setSegmentList] = useState<IUserSegment[]>(props.segmentList);
  const [allUserId, setAllUserId] = useState<number>(props.allUserId || 0);

  // handlers
  const onCheckItem = (id: number, checked: boolean) => {
    searchRef.current?.clear();
    let newIds: number[] = [];
    if (checked) {
      newIds = selectedIds.concat(id);
    } else {
      const pos = selectedIds.indexOf(id);
      newIds = selectedIds.slice(0, pos).concat(selectedIds.slice(pos + 1, selectedIds.length));
    }
    setSelectedIds(newIds);
    props.onChangeSelectedIds(newIds);
  };

  const onCancelUserId = () => {
    setSelectedIds([]);
    props.onChangeSelectedIds([]);
  };

  // watch
  useEffect(() => {}, [selectedIds]);

  useEffect(() => {
    setSegmentList(props.segmentList);
    setSelectedIds(props.selectedIds);
    setAllUserId(props.allUserId || 0);
  }, [props]);

  // init
  useEffect(() => {
    props.onChangeSelectedIds([]);
  }, []);

  return (
    <Box className={classes.root}>
      <Accordion defaultExpanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" justifyContent="space-between" width={'100%'}>
            <Typography component="h6" variant="subtitle1" style={{ fontWeight: 500, cursor: 'move' }}>
              Home Curation
            </Typography>
            <IconButton
              onClick={e => {
                e.stopPropagation();
                props.forceRefresh();
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box width="100%">
            <Box width="300px" marginBottom="13px">
              <SearchTextField placeholder="User ID" fullWidth onValueSubmit={props.onChangeUserId} onCancel={onCancelUserId} ref={searchRef} />
            </Box>
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" key={`seg-lit-item-0`}>
              <Box display="flex" flexDirection="row" alignItems="center">
                <Tooltip title="Active" arrow>
                  <LensFilled className={cx(classes.statusIcon, classes.active)} />
                </Tooltip>
                <Checkbox checked={true} disabled={true} size="small" />
                <Tooltip title={`All user`} arrow>
                  <Chip size="small" label={allUserId} color="default" style={{ marginRight: 8, width: 30 }} />
                </Tooltip>
                <Typography variant="body2" className={classes.itemTitle} component="div">
                  <Chip size="small" label="Default" color="primary" style={{ marginRight: 8 }} />
                  <Box display="flex" flexDirection="column">
                    All user
                  </Box>
                </Typography>
              </Box>
            </Box>
            {[...segmentList]
              .sort((a, b) => {
                return selectedIds.indexOf(a.id!) >= 0 ? -1 : a.id! > b.id! ? 0 : 1;
              })
              .map(item => {
                const checked = selectedIds.indexOf(item.id!) >= 0;

                return (
                  <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" key={`seg-lit-item-${item.id!}`}>
                    <Box display="flex" flexDirection="row" alignItems="center">
                      {item.isActive && (
                        <Tooltip title="Active" arrow>
                          <LensFilled className={cx(classes.statusIcon, classes.active)} />
                        </Tooltip>
                      )}
                      {!item.isActive && (
                        <Tooltip title="Inactive" arrow>
                          <LensFilled className={cx(classes.statusIcon, classes.inactive)} />
                        </Tooltip>
                      )}
                      <Checkbox
                        checked={checked || item.id === allUserId}
                        disabled={item.id === allUserId}
                        size="small"
                        onChange={e => {
                          onCheckItem(item.id!, e.target.checked);
                        }}
                      />
                      <Tooltip title={`Segment Id is ${item.id!}`} arrow>
                        <Chip size="small" label={item.id!} color="default" style={{ marginRight: 8, width: 30 }} />
                      </Tooltip>
                      <Typography variant="body2" className={classes.itemTitle} component="div">
                        {item.isGreedy && <Chip size="small" label="Greedy" color="secondary" style={{ marginRight: 8 }} />}
                        <Box display="flex" flexDirection="column">
                          {item.name}
                        </Box>
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
